import { gql } from '@apollo/client';
import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Select from '../../components/Select';
import type { SeriesSelect_SeriesFragment } from '../../graphql/generated/graphqlTypes';
import useSeriesPolicyCheck, {
  SeriesPolicyAction,
} from '../useSeriesPolicyCheck';
import SeriesOption from './SeriesOption';
import styles from './SeriesSelect.module.scss';
import SeriesSelectValue from './SeriesSelectValue';

const SERIES_SELECT_POST_FRAGMENT = gql`
  fragment SeriesSelect_post on Post {
    id
    title
    isPublic
    ...useSeriesPolicyCheck_post
  }
  ${useSeriesPolicyCheck.fragments.post}
`;

const SERIES_SELECT_SERIES_FRAGMENT = gql`
  fragment SeriesSelect_series on Series {
    id
    name
    posts {
      ...SeriesSelect_post
    }
    ...useSeriesPolicyCheck_series
  }
  ${useSeriesPolicyCheck.fragments.series}
  ${SERIES_SELECT_POST_FRAGMENT}
`;

interface SeriesSelectProps<Series extends SeriesSelect_SeriesFragment> {
  series: Series | null;
  setSeries(series: Series): void;
  options: Series[];
}

const SeriesSelect = <Series extends SeriesSelect_SeriesFragment>({
  series,
  setSeries,
  options,
}: SeriesSelectProps<Series>) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const seriesPolicyCheck = useSeriesPolicyCheck();

  const [inputValue, setInputValue] = useState('');

  return (
    <Select<{ series: Series; isDisabled: boolean }, false, false>
      autoFocus
      blurInputOnSelect
      className={styles.select}
      openMenuOnClick
      simpleValue={false}
      components={{
        SingleValue: SeriesSelectValue,
        Option: SeriesOption,
      }}
      getOptionLabel={({ series: { name } }) => name}
      getOptionValue={({ series: { id } }) => id}
      filterOption={({ label }, filter) =>
        label.toLowerCase().includes(filter.toLowerCase())
      }
      inputValue={inputValue}
      onInputChange={(newValue) => setInputValue(newValue)}
      value={series ? { series, isDisabled: false } : null}
      onChange={(option) => {
        if (option) {
          setSeries(option.series);
        }
      }}
      options={options.map((option) => ({
        series: option,
        isDisabled: !seriesPolicyCheck(
          SeriesPolicyAction.EditSeries,
          option,
          currentUser,
        ),
      }))}
    />
  );
};

SeriesSelect.fragments = {
  series: SERIES_SELECT_SERIES_FRAGMENT,
  post: SERIES_SELECT_POST_FRAGMENT,
};

export default SeriesSelect;
